import React from 'react';

import {
  Box,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';

import type { PhotoUrlType } from '@/domain/photo/redux';
import type { PhotoItemJson } from '@/types/domain/photo/entities/photoItemEntity';

import CardMediaImage from '@/components/CardMediaImage';
import PhotoModal from '@/components/PhotoModal';

/**
 * PurchasePhotoType
 */
export type PurchasePhotoType = {
  /** 名前 */
  name: string,
  /** ID */
  id: string,
  /** URL */
  url?: PhotoUrlType,
  /** 写真タイプリスト */
  photoTypes: {
    /** タイプ */
    type: PhotoItemJson,
    /** 数 */
    num: number,
  }[],
}

/**
 * プロパティ
 */
export type Props = {
  /** 施設ID */
  nurseryId: string,
  /** 購入写真リスト */
  purchasePhotoList: PurchasePhotoType[],
  /** 写真タイプをセット */
  setPhotoType: (
    isChecked: boolean,
    photoType: PhotoItemJson,
    photoId: string,
    num: number,
  ) => void,
  /** 写真タイプ数をセット */
  setPhotoTypeNum: (
    photoType: PhotoItemJson,
    photoId: string,
    num: number,
  ) => void,
  /** 写真タイプリスト */
  photoTypeList: PhotoItemJson[],
  /** selectPhotoTypeAll */
  selectPhotoTypeAll: (isChecked: boolean, photoType: PhotoItemJson) => void,
};

const SelectPhotoType: React.FC<Props> = ({
  nurseryId,
  purchasePhotoList,
  setPhotoType,
  setPhotoTypeNum,
  photoTypeList,
  selectPhotoTypeAll,
}) => {
  const [previewS3Path, setPreviewS3Path] = React.useState<string | undefined>(undefined);
  return (
    <>
      <div style={{ marginTop: '20px' }}>
        {photoTypeList.map((v) => (
          <FormControlLabel
            key={v.photoItemId}
            control={(
              <Checkbox
                onChange={(e) => selectPhotoTypeAll(e.target.checked, { ...v })}
                defaultChecked={v.photoItemId === 'download'}
              />
            )}
            label={`全選択(${v.name},${v.price}円)`}
          />
        ))}
        <Grid
          container
          spacing={2}
          columns={{
            xs: 4,
            sm: 4,
            md: 8,
            lg: 12,
          }}
        >
          {purchasePhotoList.map((item) => {
            const labels = [];
            for (let i = 0; i < photoTypeList.length; i += 1) {
              const target = item.photoTypes.find(
                (v) => v.type.photoItemId === photoTypeList[i].photoItemId,
              );
              const isInclude = target !== undefined;
              const num = target ? target.num : 1;
              labels.push(
                <div key={photoTypeList[i].photoItemId}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={isInclude}
                        onChange={
                          (e) => setPhotoType(
                            e.target.checked,
                            { ...photoTypeList[i] },
                            item.id,
                            1,
                          )
                        }
                      />
                    )}
                    label={photoTypeList[i].name}
                    defaultChecked={isInclude}
                  />
                  {photoTypeList[i].photoItemId !== 'download' && (
                    <FormControl
                      variant="standard"
                      sx={{ m: 1, minWidth: 120 }}
                      disabled={!isInclude}
                    >
                      <InputLabel>{`購入枚数(${photoTypeList[i].name})`}</InputLabel>
                      <Select
                        value={num}
                        onChange={(e) => setPhotoTypeNum(
                          { ...photoTypeList[i] },
                          item.id,
                          e.target.value as number,
                        )}
                        label={`購入枚数(${photoTypeList[i].name})`}
                      >
                        {[...Array(10)].map((_v, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <MenuItem key={`index-${i}`} value={i + 1}>{i + 1}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </div>,
              );
            }
            return (
              <Grid key={item.id} item xs={4} sm={4} md={4} lg={4}>
                <Card sx={{ display: 'flex', width: '100%' }}>
                  <CardMediaImage
                    nurseryId={nurseryId}
                    s3Path={`WagakoNurseryPhoto_PhotoEntity/${item.id}/thumbnail.webp`}
                    onClick={() => setPreviewS3Path(`WagakoNurseryPhoto_PhotoEntity/${item.id}/sample.webp` || 'images/loading.png')}
                    alt={item.id}
                    width={200}
                    height={200}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', margin: '10px' }}>
                    <FormGroup>
                      {item.name}
                      {labels}
                    </FormGroup>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </div>
      {previewS3Path && (
        <PhotoModal
          s3Path={previewS3Path}
          close={() => setPreviewS3Path(undefined)}
          nurseryId={nurseryId}
        />
      )}
    </>
  );
};

export default SelectPhotoType;
